import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, AlertCircle, Loader2, Mail, KeyRound, Eye, EyeOff, Users, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Parent / Guardian invite acceptance page.
 *
 * Twin of AthleteInvite.jsx but hits /parent-invites/* endpoints. On success,
 * the parent is signed straight in — the backend hijacks role to "athlete"
 * with the linked athlete's email, so every existing athlete-scope screen
 * (workouts, calendar, readiness, results) works with zero UI changes.
 */
export default function ParentInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [phase, setPhase] = useState("loading"); // loading | ready | done | error
  const [error, setError] = useState("");
  const [invite, setInvite] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/parent-invites/${token}`);
        if (cancelled) return;
        setInvite(data);
        setName(data.parent_name || "");
        setPhase("ready");
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "This invitation link is invalid or has expired.");
        setPhase("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/parent-invites/${token}/accept`, { password, name });
      if (data?.token) {
        localStorage.setItem("token", data.token);
        try { await refreshUser(); } catch { /* non-fatal */ }
      }
      setPhase("done");
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Couldn't complete registration. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (phase === "loading") {
    return (
      <Shell>
        <div className="text-center" data-testid="parent-invite-loading">
          <Loader2 className="w-8 h-8 text-[#7C3AED] animate-spin mx-auto mb-4" />
          <div className="overline text-[#A78BFA]">// verifying your invitation</div>
        </div>
      </Shell>
    );
  }

  if (phase === "error") {
    return (
      <Shell>
        <div className="text-center max-w-md" data-testid="parent-invite-error">
          <AlertCircle className="w-16 h-16 text-[#FF3B30] mx-auto mb-6" />
          <div className="overline text-[#FF3B30] mb-2">// invitation problem</div>
          <h2 className="font-heading text-3xl font-black uppercase mb-4">Link not valid</h2>
          <p className="text-[#A1A1AA] text-sm mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-block px-6 py-3 border border-white/20 hover:border-white/40 font-heading text-xs uppercase tracking-[0.25em]"
          >
            Go to Login
          </Link>
        </div>
      </Shell>
    );
  }

  if (phase === "done") {
    return (
      <Shell>
        <div className="text-center max-w-md" data-testid="parent-invite-done">
          <CheckCircle2 className="w-16 h-16 text-[#34C759] mx-auto mb-6" />
          <div className="overline text-[#34C759] mb-2">// account activated</div>
          <h2 className="font-heading text-3xl font-black uppercase mb-4">You're In!</h2>
          <p className="text-[#A1A1AA] text-sm mb-6">
            Welcome, <span className="text-white font-bold">{name}</span> — you can now view and log
            workouts for <b className="text-white">{invite.athlete_name}</b>.
          </p>
          <button
            onClick={() => navigate("/")}
            data-testid="parent-invite-continue"
            className="inline-block px-6 py-3 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-heading text-xs uppercase tracking-[0.25em]"
          >
            Continue to Dashboard
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="parent-invite-ready">
        <div className="text-center mb-3">
          <Users className="w-10 h-10 text-[#22D3EE] mx-auto mb-3" />
          <div className="overline text-[#22D3EE] mb-1">// parent / guardian access</div>
          <h1 className="font-heading text-3xl font-black uppercase">Set Your Password</h1>
          <p className="text-[#A1A1AA] text-sm mt-2 leading-relaxed">
            You've been invited to view and log workouts for{" "}
            <b className="text-white">{invite.athlete_name}</b>.
          </p>
        </div>

        <FormField label="Your Name" icon={<User size={12} />}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="parent-invite-name"
            required
            className="w-full bg-transparent border-b border-white/20 focus:border-[#22D3EE] outline-none py-2 text-base"
          />
        </FormField>

        <FormField label="Email" icon={<Mail size={12} />}>
          <input
            type="email"
            value={invite.parent_email}
            disabled
            data-testid="parent-invite-email"
            className="w-full bg-transparent border-b border-white/10 py-2 text-base text-[#A1A1AA] cursor-not-allowed"
          />
        </FormField>

        <FormField label="Password" icon={<KeyRound size={12} />}>
          <div className="relative">
            <input
              type={show ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="parent-invite-password"
              required
              minLength={8}
              className="w-full bg-transparent border-b border-white/20 focus:border-[#22D3EE] outline-none py-2 pr-9 text-base"
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute right-0 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white"
              aria-label={show ? "Hide password" : "Show password"}
            >
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <div className="text-[10px] text-[#71717A] mt-1">At least 8 characters.</div>
        </FormField>

        <FormField label="Confirm Password" icon={<KeyRound size={12} />}>
          <input
            type={show ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="parent-invite-confirm"
            required
            className="w-full bg-transparent border-b border-white/20 focus:border-[#22D3EE] outline-none py-2 text-base"
          />
        </FormField>

        {error && (
          <div className="text-[#FF3B30] text-xs" data-testid="parent-invite-form-error">{error}</div>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid="parent-invite-submit"
          className="w-full py-3 bg-[#22D3EE] hover:bg-[#0EA5E9] disabled:opacity-50 text-black font-heading text-xs uppercase tracking-[0.25em] font-bold flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : null}
          {busy ? "Activating…" : "Activate Account"}
        </button>

        <p className="text-[10px] text-[#71717A] text-center pt-2">
          After activation your account signs in immediately.
        </p>
      </form>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
      {children}
    </div>
  );
}

function FormField({ label, icon, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#71717A] mb-1 flex items-center gap-1.5">
        {icon} {label}
      </span>
      {children}
    </label>
  );
}
